import { ApolloClient, ApolloError, useApolloClient, useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { GetUserRitualsQuery, GetUserRitualsQueryVariables } from '../../client/generated/graphql';
import { getUserRitualsQuery } from '../../client/queries/userRituals';

const readRitualIdsFromCache = (client: ApolloClient<object>) => {
    const cacheData = client.cache.extract();
    const userEntry = Object.entries(cacheData).find(([key]) => key.startsWith('User:'));

    if (userEntry?.[1]) {
        const userData = userEntry[1] as { morningRitualId?: string | null; eveningRitualId?: string | null };
        if ('morningRitualId' in userData || 'eveningRitualId' in userData) {
            return {
                morningRitualId: userData.morningRitualId ?? null,
                eveningRitualId: userData.eveningRitualId ?? null,
            };
        }
    }
    return null;
};

export const useCachedUserGoalCompletion = (): string => {
    const client = useApolloClient();

    return useMemo(() => {
        const cacheData = client.cache.extract();
        const userEntry = Object.entries(cacheData).find(([key]) => key.startsWith('User:'));

        if (userEntry && userEntry[1]) {
            const userData = userEntry[1] as any;
            return userData.goalCompletion || '';
        }

        return '';
    }, [client.cache]);
};

interface CachedRitualQuestIds {
    morningRitualId: string | null;
    eveningRitualId: string | null;
    loading: boolean;
    error: ApolloError | undefined;
}

export const useCachedRitualQuestIds = (): CachedRitualQuestIds => {
    const client = useApolloClient();
    const cachedData = readRitualIdsFromCache(client);

    const { data, loading, error } = useQuery<GetUserRitualsQuery, GetUserRitualsQueryVariables>(getUserRitualsQuery, {
        skip: !!cachedData,
        fetchPolicy: 'cache-first',
    });

    if (cachedData) {
        return {
            morningRitualId: cachedData.morningRitualId,
            eveningRitualId: cachedData.eveningRitualId,
            loading: false,
            error: undefined,
        };
    }

    return {
        morningRitualId: data?.user?.morningRitualId ?? null,
        eveningRitualId: data?.user?.eveningRitualId ?? null,
        loading,
        error,
    };
};
